import { Drawer, Skeleton } from 'antd'
import { getAppFrameElement } from './AppViewport'
import type { AnchorUserChatSummary } from '@/types'

interface UserChatSummaryDrawerProps {
  open: boolean
  loading: boolean
  error?: string
  summary: AnchorUserChatSummary | null
  onClose: () => void
}

function listItems(values?: string[]) {
  return (values || []).map((value) => value.trim()).filter(Boolean)
}

export function UserChatSummaryDrawer({ open, loading, error, summary, onClose }: UserChatSummaryDrawerProps) {
  const structured = summary?.structured_summary || {}
  const tips = listItems(structured.conversation_tips)
  const topics = listItems(structured.recent_topics)
  const preferences = listItems(structured.stable_preferences)
  const mood = structured.emotional_state || structured.mood || ''
  const hasSummary = Boolean(summary?.available && summary.summary_text)
  const drawerContainer = getAppFrameElement() || false

  return (
    <Drawer
      rootClassName="user-chat-summary-drawer"
      autoFocus={false}
      closeIcon={null}
      height="48%"
      mask={false}
      open={open}
      placement="bottom"
      title={
        <div className="user-chat-summary-header">
          <div>
            <h2>Chat summary</h2>
            <p>AI-generated user context</p>
          </div>
          <button type="button" aria-label="Close chat summary" onClick={onClose}>
            Close
          </button>
        </div>
      }
      zIndex={2600}
      getContainer={drawerContainer}
      onClose={onClose}
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : error ? (
        <div className="user-chat-summary-empty">{error}</div>
      ) : !hasSummary ? (
        <div className="user-chat-summary-empty">No summary available</div>
      ) : (
        <div className="user-chat-summary-content">
          {tips.length > 0 ? <SummarySection title="Conversation tips" items={tips} /> : null}
          <div className="user-chat-summary-highlight">
            <span>Summary</span>
            <p className="user-chat-summary-text">{summary?.summary_text}</p>
            {structured.one_line ? <p className="user-chat-summary-one-line">{structured.one_line}</p> : null}
          </div>
          {mood ? <SummarySection title="Mood" items={[mood]} /> : null}
          {preferences.length > 0 ? <SummarySection title="Preferences" items={preferences} /> : null}
          {topics.length > 0 ? <SummarySection title="Recent topics" items={topics} /> : null}
        </div>
      )}
    </Drawer>
  )
}

function SummarySection({ title, items }: { title: string; items: string[] }) {
  return (
    <section className="user-chat-summary-section">
      <h3>{title}</h3>
      <ul>
        {items.map((item) => (
          <li key={`${title}-${item}`}>{item}</li>
        ))}
      </ul>
    </section>
  )
}
